// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Collapse, Dropdown } from "react-bootstrap";

import {
  IAction,
  ActionType,
  raiseAction,
  IActionSortTable,
  IActionChangeTariff,
  IActionTypeConfirmPopup,
} from "actions";
import { getLocalDate, isSet } from "utils";
import { makeSortHeader, tabClickHandler } from "guiutils";
import { IState } from "states/states";
import { ITariff } from "states/ocppstates";
import { ITableSort } from "states/guistates";

import { ReactComponent as IconEdit } from "static/iconEdit.svg";
import { ReactComponent as IconDelete } from "static/iconDelete.svg";
import { ReactComponent as IconExpandMore } from "static/iconExpandMore.svg";
import { ReactComponent as IconExpandLess } from "static/iconExpandLess.svg";

import ModalTariffSettings from "view/modal/ModalTariffSettings";
import ModalAddGlobalTariff from "view/modal/ModalAddGlobalTariff";
import FormattedTime from "./FormattedTime";

// sort key of table
const TABLE_NAME = "t_tariffs";

interface ITariffsProps {
  siteId: number;
  tariffs: ITariff[];
  isReadOnly: boolean;
  tableSort: ITableSort | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: ITariffsProps = {
    siteId: state.cached.selectedSite!,
    tariffs: state.ocpp.siteTariff,
    isReadOnly: false,
    tableSort: state.gui.tableSort[TABLE_NAME],
  };
  return props;
};

interface ITariffsState {
  isExpanded: boolean;
  isNewButtonExpanded: boolean;
}

interface ITariffsDispatch {
  onSort: (sort: IActionSortTable) => void;
  onEdit: (id: number) => void;
  onNewCustomTariff: () => void;
  onNewGlobalTariff: () => void;
  onConfirmDelete: (id: number, title: string, message: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITariffsDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
    onEdit: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_TARIFF, id));
    },
    onNewCustomTariff: () => {
      dispatch(raiseAction(ActionType.ACT_EDIT_TARIFF, undefined));
    },
    onNewGlobalTariff: () => {
      dispatch(raiseAction(ActionType.ACT_NEW_GLOBAL_TARIFF));
    },
    onConfirmDelete: (id: number, title: string, message: string) => {
      const confirmPayload: IActionChangeTariff = {
        id,
        name: undefined,
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_SAVE_OCPP_TARIFF_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
      // the delete operation takes some time so the screen update will not be instantenious
      // as the confirm popup is closed immediately
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msgEdit: {
    id: "edit tariff",
  },
  msgDelete: {
    id: "delete tariff",
  },
  confirmDeleteTitle: {
    id: "Delete cost tariff",
  },
  confirmDeleteBody: {
    id: "This will delete this tariff. All associated costs will be lost. Are you sure?",
  },
});

class TariffsComponent extends React.Component<
  ITariffsProps & ITariffsDispatch & WrappedComponentProps,
  ITariffsState
> {
  state: ITariffsState = {
    isExpanded: true,
    isNewButtonExpanded: false,
  };

  public render() {
    const showPriority = this.props.tariffs.length > 1;
    const sortCol = this.props.tableSort?.column as keyof ITariff | undefined;
    const tariffItems = Array.from(Array(this.props.tariffs.length).keys())
      // global tariffs not associated to this site have isActive === undefined are shall not be shown
      .filter((index) => isSet(this.props.tariffs[index].isActive))
      .sort((a, b) => {
        let keyA = this.props.tariffs[a]?.[sortCol!];
        let keyB = this.props.tariffs[b]?.[sortCol!];
        // special handling for special columns
        if (sortCol === "timeStart") {
          // column is used for start and end time; just sum then up, even if not necessarily correct
          keyA =
            (this.props.tariffs[a].timeStart ?? 0) + (this.props.tariffs[a].timeEnd ?? 0) * 100000;
          keyB =
            (this.props.tariffs[b].timeStart ?? 0) + (this.props.tariffs[b].timeEnd ?? 0) * 100000;
        }
        const sort = isSet(keyA) && isSet(keyB) ? (keyA > keyB ? 1 : -1) : isSet(keyB) ? 1 : -1;
        return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
      })
      .map((curItem) => {
        return this.renderTariff(curItem, showPriority);
      });

    return (
      <div id="tariffs">
        {!this.props.isReadOnly && this.state.isExpanded && (
          <div id="tariff-buttonbox" className="mt-2">
            <Dropdown
              show={this.state.isNewButtonExpanded}
              onToggle={() =>
                this.setState({ isNewButtonExpanded: !this.state.isNewButtonExpanded })
              }
            >
              <Dropdown.Toggle variant="secondary">
                <FormattedMessage id="New Tariff" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={(e) => this.props.onNewCustomTariff()}>
                  <FormattedMessage id="New Site Tariff" />
                </Dropdown.Item>
                <Dropdown.Item onClick={(e) => this.props.onNewGlobalTariff()}>
                  <FormattedMessage id="Add Global Tariff" />
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
        <h1
          className="link"
          onClick={() => {
            this.setState({ isExpanded: !this.state.isExpanded });
          }}
        >
          <Button variant="link" className="expand-button me-2 d-print-none">
            {this.state.isExpanded ? <IconExpandMore /> : <IconExpandLess />}
          </Button>
          <FormattedMessage id="Cost Tariffs" />
        </h1>
        <Collapse in={this.state.isExpanded}>
          <div id="tarifftable">
            {tariffItems.length > 0 ? (
              <table className="datatable fullwidthwhite">
                <thead>
                  <tr>
                    {this.makeSortHeader("name", <FormattedMessage id="Name" />)}
                    {showPriority &&
                      this.makeSortHeader("priority", <FormattedMessage id="Priority" />, [
                        "optional2",
                      ])}
                    {this.makeSortHeader(
                      "costPerEnergy",
                      <FormattedMessage id="Cost per Energy" />,
                    )}
                    {this.makeSortHeader(
                      "costPerPeriod",
                      <FormattedMessage id="Cost per Period" />,
                    )}
                    {this.makeSortHeader("validFrom", <FormattedMessage id="Validity" />, [
                      "optional",
                    ])}
                    {this.makeSortHeader("timeStart", <FormattedMessage id="Time Restriction" />, [
                      "optional",
                    ])}
                    {!this.props.isReadOnly && <th></th>}
                  </tr>
                </thead>
                <tbody>{tariffItems}</tbody>
              </table>
            ) : (
              <div style={{ paddingTop: 5, paddingBottom: 5 }}>
                <FormattedMessage id="No tariff added yet." />
              </div>
            )}
          </div>
        </Collapse>
        <ModalTariffSettings />
        <ModalAddGlobalTariff />
      </div>
    );
  }

  // this function is just a short-cut to reduce boilerplate
  private makeSortHeader(
    column: keyof ITariff,
    caption: JSX.Element,
    classes?: string[],
    title?: string,
  ) {
    return makeSortHeader(
      TABLE_NAME,
      column,
      caption,
      this.props.tableSort,
      this.props.onSort,
      classes,
      title,
    );
  }

  private renderTariff(index: number, showPriority: boolean) {
    const tariff = this.props.tariffs[index];
    const periodRestriction: string[] = [];
    if (tariff.validFrom || tariff.validUntil) {
      periodRestriction.push(
        `${tariff.validFrom ? getLocalDate(tariff.validFrom * 1000) : ""} - ${
          tariff.validUntil ? getLocalDate(tariff.validUntil * 1000) : ""
        }`,
      );
    }
    // we need to construct the new Date() at the current day, otherwise the
    // hours might be wrong due to winterzeit/sommerzeit
    const dateOffset = Date.now() - (Date.now() % (24 * 3600 * 1000));

    return (
      <tr
        key={index}
        className={!tariff.isActive || this.isTariffExpired(tariff) ? "deactivated" : undefined}
      >
        <td>{tariff.name}</td>
        {showPriority && <td className="optional2">{tariff.priority}</td>}
        <td>
          {isSet(tariff.costPerEnergy) ? (
            <>
              {tariff.costPerEnergy} {tariff.currency}/kWh
            </>
          ) : (
            "-"
          )}
        </td>
        <td>
          {isSet(tariff.costPerPeriod) ? (
            <>
              {tariff.costPerPeriod} {tariff.currency}/h
            </>
          ) : (
            "-"
          )}
        </td>
        <td className="optional">
          {periodRestriction.length ? periodRestriction.join(", ") : "-"}
        </td>
        <td className="optional">
          {isSet(tariff.timeStart) ? (
            <>
              <FormattedTime valueMs={tariff.timeStart * 1000 + dateOffset} /> -{" "}
              <FormattedTime valueMs={tariff.timeEnd! * 1000 + dateOffset} />
            </>
          ) : (
            "-"
          )}
        </td>
        {!this.props.isReadOnly &&
          (this.props.siteId === tariff.siteId || !isSet(tariff.siteId)) && (
            <td className="edit-col img-link nowrap">
              <span
                tabIndex={0}
                onKeyDown={tabClickHandler}
                onClick={(_evt) => this.props.onEdit(tariff.id)}
                title={this.props.intl.formatMessage(messages.msgEdit)}
              >
                <IconEdit />
              </span>
              {false && (
                <span
                  className="ms-1"
                  tabIndex={0}
                  onKeyDown={tabClickHandler}
                  onClick={(_evt) =>
                    this.props.onConfirmDelete(
                      tariff.id,
                      this.props.intl.formatMessage(messages.confirmDeleteTitle),
                      this.props.intl.formatMessage(messages.confirmDeleteBody),
                    )
                  }
                  title={this.props.intl.formatMessage(messages.msgDelete)}
                >
                  <IconDelete />
                </span>
              )}
            </td>
          )}
      </tr>
    );
  }

  private isTariffExpired(tariff: ITariff) {
    // check limits
    let expired = false; // default
    if (tariff.validFrom && tariff.validFrom > Date.now() / 1000) {
      expired = true;
    } else if (tariff.validUntil && tariff.validUntil < Date.now() / 1000) {
      expired = true;
    }
    return expired;
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(TariffsComponent));
